import {
  Component,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';

/**
 * Confirmation dialog for blocking a user from the super admin Users list (SC-3089).
 *
 * Collects the required internal reason and the optional reason shown to the client, then calls
 * POST v2/admin/users/{id}/block. Closes with true on success so the list can refresh.
 *
 * Angular escapes interpolated values in this template, so nothing user controlled is rendered as HTML.
 */
@Component({
  selector: 'app-users-block-dialog',
  templateUrl: './users-block-dialog.component.html',
  styleUrls: ['./users-block-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsersBlockDialogComponent {
  readonly reasonMinLength: number = 5;
  readonly reasonMaxLength: number = 500;

  // Internal reason, only super admins can see this.
  reason: string = '';
  // Optional reason shown to the client in their Employees list.
  clientNote: string = '';
  // Guards against a double submit while the request is running.
  inProgress: boolean = false;

  constructor(
    public utils: UtilsService,
    private api: ApiRequestService,
    @Inject(MAT_DIALOG_DATA) public data: { id: number; accounts_count: number; is_partner: boolean },
    private dialogRef: MatDialogRef<UsersBlockDialogComponent>
  ) {}

  /**
   * The block button is enabled only when the trimmed reasons are within the API limits.
   */
  get canSubmit(): boolean {
    const reason = (this.reason || '').trim();
    const note = (this.clientNote || '').trim();
    return (
      !this.inProgress &&
      reason.length >= this.reasonMinLength &&
      reason.length <= this.reasonMaxLength &&
      note.length <= this.reasonMaxLength
    );
  }

  /**
   * Block the user. The API answers 409 or 422 with a message when the block is refused, which is
   * shown as an error while the dialog stays open.
   */
  onBlock() {
    if (!this.canSubmit) {
      return;
    }
    this.inProgress = true;
    const note = (this.clientNote || '').trim();
    this.api
      .makeRequest('post', `v2/admin/users/${this.data.id}/block`, {
        reason: (this.reason || '').trim(),
        client_note: note ? note : null
      })
      .then((response) => {
        this.utils.showToast(
          response && response.message
            ? response.message
            : 'The user has been blocked.'
        );
        this.dialogRef.close(true);
      })
      .catch((errorResponse) => {
        this.inProgress = false;
        this.utils.handleAPIErrors(errorResponse);
      });
  }
}
